import { rem } from '@mantine/core';
import { useAiMutations, useLatestAiSummary } from '../api/ai';
import { AiPill, refreshedAt, SMALL_BTN } from '../components/AiBits';
import { summaryBehind } from './derive';
import { CARD, MONO, SECTION_LABEL } from './ui';

/**
 * POVZETEK KOMUNIKACIJE — the stored Gemini summary of the thread, shown on
 * Profil. Opening a profile never generates one: Komunikacija refreshes it when
 * the thread changed, and "Osveži povzetek" does on demand. Bullets cite
 * messages as [n], and clicking one opens the thread at the source message.
 */
export function AiSummaryCard({
  volunteerId,
  enabled,
  messageIds,
  waiting,
  onHighlight,
}: {
  volunteerId: string;
  enabled: boolean;
  /** Thread message ids in display order (to number citations); `null` until the thread loads. */
  messageIds: string[] | null;
  /**
   * Who owes an answer, as `commsTurn` reads the conversation now — not the
   * summary's own reading, which is as old as the summary.
   */
  waiting: string | null;
  onHighlight: (messageId: string) => void;
}) {
  const summary = useLatestAiSummary(volunteerId, enabled);
  const mut = useAiMutations();
  const s = summary.data ?? null;
  const busy = summary.isFetching || mut.refreshSummary.isPending;
  // The summary is true of the thread it was written over, so one that is
  // behind keeps its bullets and says so.
  const behind = summaryBehind(s, messageIds);
  const warn = waiting;

  return (
    <div style={{ ...CARD, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={SECTION_LABEL}>POVZETEK KOMUNIKACIJE</span>
        <div style={{ flex: 1 }} />
        <AiPill>{enabled ? 'Gemini · povzetek' : 'AI izklopljen'}</AiPill>
      </div>

      {warn ? (
        <div
          style={{
            marginBottom: 10,
            padding: '8px 10px',
            borderRadius: 7,
            background: 'oklch(0.985 0.02 25)',
            border: '1px solid oklch(0.9 0.04 25)',
            fontSize: rem(12.5),
            color: 'oklch(0.45 0.1 25)',
            lineHeight: 1.4,
          }}
        >
          {warn}
        </div>
      ) : null}

      {behind ? (
        <div style={{ marginBottom: 10, fontSize: rem(12), color: 'oklch(0.55 0.012 90)', lineHeight: 1.4 }}>
          Pogovor se je od povzetka spremenil — osveži povzetek.
        </div>
      ) : null}

      {busy && !s ? (
        <div style={{ fontSize: rem(13), color: 'oklch(0.55 0.012 90)', padding: '10px 2px' }}>Berem pogovor…</div>
      ) : null}

      {s ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, opacity: busy ? 0.6 : 1 }}>
          {s.bullets.map((b, i) => {
            const n = b.messageId && messageIds ? messageIds.indexOf(b.messageId) + 1 : 0;
            return (
              <div key={i} style={{ display: 'flex', gap: 7, fontSize: rem(13.5), lineHeight: 1.45, color: 'oklch(0.3 0.012 90)' }}>
                <span style={{ color: 'oklch(0.6 0.012 90)', flexShrink: 0 }}>—</span>
                <span>
                  {b.text}
                  {n > 0 ? (
                    <button
                      onClick={() => onHighlight(b.messageId!)}
                      title="odpri izvorno sporočilo v zavihku Komunikacija"
                      style={{
                        marginLeft: 5,
                        border: 0,
                        background: 'none',
                        cursor: 'pointer',
                        fontFamily: MONO,
                        fontSize: rem(11.5),
                        color: 'oklch(0.5 0.09 245)',
                        padding: 0,
                      }}
                    >
                      [{n}]
                    </button>
                  ) : null}
                </span>
              </div>
            );
          })}
          {s.bullets.length === 0 ? (
            <div style={{ fontSize: rem(13), color: 'oklch(0.55 0.012 90)' }}>Ni še dovolj pogovora za povzetek.</div>
          ) : null}
        </div>
      ) : !busy ? (
        <div style={{ fontSize: rem(12.5), color: 'oklch(0.55 0.012 90)', lineHeight: 1.5 }}>
          {enabled
            ? summary.isError
              ? 'Povzetka ni bilo mogoče pripraviti.'
              : 'Še ni povzetka.'
            : 'AI pomočnik ni vklopljen. Sporočila so v zavihku Komunikacija.'}
        </div>
      ) : null}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 11 }}>
        <button
          onClick={() => mut.refreshSummary.mutate(volunteerId)}
          disabled={!enabled || busy}
          style={{ ...SMALL_BTN, cursor: enabled && !busy ? 'pointer' : 'default', opacity: enabled ? 1 : 0.6 }}
        >
          {busy ? 'Berem pogovor…' : 'Osveži povzetek'}
        </button>
        {s ? <span style={{ fontSize: rem(11.5), color: 'oklch(0.62 0.012 90)' }}>{refreshedAt(s.createdAt)}</span> : null}
      </div>
      <div style={{ marginTop: 9, fontSize: rem(11.5), color: 'oklch(0.64 0.012 90)', lineHeight: 1.4 }}>
        Povzetek je strojno ustvarjen — številke v oklepajih vodijo do izvornega sporočila.
      </div>
    </div>
  );
}
